import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Crown, Target, Users } from '@phosphor-icons/react';

const LandingPage = () => {
  const { login, register } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'pt' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(email, password, displayName);
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* Hero Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-white via-gray-50 to-white -z-10">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at center, rgba(204, 255, 0, 0.08) 0%, rgba(255, 255, 255, 0) 70%)' }}></div>
      </div>

      {/* Language Toggle */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20">
        <button
          onClick={toggleLanguage}
          data-testid="landing-language-toggle"
          className="px-5 py-3 text-base font-mono uppercase font-black border-3 border-black bg-primary text-black hover:bg-primary-hover transition-all rounded-sm shadow-xl"
        >
          {i18n.language === 'en' ? 'PT-BR' : 'ENGLISH'}
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center py-12 md:py-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Hero */}
            <div className="space-y-6 md:space-y-8">
              <div className="flex items-center space-x-3 md:space-x-4">
                <div className="relative w-16 h-16 md:w-20 md:h-20 bg-black border-3 md:border-4 border-[#FFD700] rounded-sm flex items-center justify-center shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                  <Crown size={28} weight="fill" className="text-[#FFD700] absolute -top-2 md:block hidden" />
                  <Crown size={24} weight="fill" className="text-[#FFD700] absolute -top-1 md:hidden" />
                  <span className="font-heading text-3xl md:text-4xl font-black text-[#FFD700] mt-3 md:mt-4">10</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-heading text-2xl md:text-3xl font-black tracking-tighter text-black leading-none">The Kings Head</span>
                  <span className="font-heading text-sm md:text-lg font-bold tracking-wider text-gray-600 leading-none">PREDICTIONS</span>
                </div>
              </div>
              
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter text-black leading-tight">
                {t('landing.hero')}
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-xl">
                {t('landing.subtitle')}
              </p>

              {/* Features */}
              <div className="grid gap-3 md:gap-4 pt-4 md:pt-8">
                <div className="flex items-start space-x-4 p-4 rounded-sm bg-black/5 border border-black/10 hover:border-primary/50 transition-all">
                  <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Target size={24} weight="bold" className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-black">{t('landing.feature1Title')}</h3>
                    <p className="text-sm text-gray-600">{t('landing.feature1Desc')}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 rounded-sm bg-black/5 border border-black/10 hover:border-accent/50 transition-all">
                  <div className="w-10 h-10 bg-accent/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Crown size={24} weight="bold" className="text-accent" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-black">{t('landing.feature2Title')}</h3>
                    <p className="text-sm text-gray-600">{t('landing.feature2Desc')}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 rounded-sm bg-black/5 border border-black/10 hover:border-secondary/50 transition-all">
                  <div className="w-10 h-10 bg-secondary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Users size={24} weight="bold" className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-black">{t('landing.feature3Title')}</h3>
                    <p className="text-sm text-gray-600">{t('landing.feature3Desc')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Auth Form */}
            <div className="bg-black/90 backdrop-blur-sm p-6 md:p-8 rounded-sm border border-white/20 max-w-md mx-auto w-full">
              <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tighter text-white mb-4 md:mb-6">
                {isLogin ? t('auth.login') : t('auth.register')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t('auth.displayName')}
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      data-testid="auth-display-name-input"
                      required
                      maxLength={30}
                      className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all"
                      placeholder={t('auth.displayNamePlaceholder')}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('auth.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="auth-email-input"
                    required
                    className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('auth.password')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="auth-password-input"
                    required
                    className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all"
                    placeholder="••••••••"
                  />
                </div>

                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t('auth.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="auth-confirm-password-input"
                      required
                      className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="auth-submit-button"
                  disabled={loading}
                  className="w-full py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all hover:scale-105 glow-effect disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : (isLogin ? t('auth.loginButton') : t('auth.registerButton'))}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-text-secondary">
                  {isLogin ? t('auth.noAccount') : t('auth.haveAccount')}
                </p>
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  data-testid="auth-switch-button"
                  className="mt-2 text-primary hover:text-primary-hover font-medium transition-colors"
                >
                  {isLogin ? t('auth.switchToRegister') : t('auth.switchToLogin')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
